import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  Plug, ExternalLink, Lock, Check, X, RefreshCw, AlertCircle,
  ChartBar, Target, TrendingUp, Database, Sparkles, BadgePercent,
  Mail, MessageCircle, Phone, Plane, Hotel, Train, Wallet,
} from "lucide-react";

const PROVIDER_ICONS = {
  api_football: Database,
  transfermarkt: TrendingUp,
  wyscout: ChartBar,
  scisports: Target,
  transferroom_club: Sparkles,
  transferroom_agent: Sparkles,
  outlook: Mail,
  gmail: Mail,
  twilio: MessageCircle,
  travelpayouts: Wallet,
  booking_com: Hotel,
  skyscanner_apify: Plane,
  omio: Train,
};

const PROVIDER_HUES = {
  api_football: "from-emerald-50 to-emerald-100 text-emerald-800 border-emerald-200",
  transfermarkt: "from-blue-50 to-blue-100 text-blue-800 border-blue-200",
  wyscout: "from-purple-50 to-purple-100 text-purple-800 border-purple-200",
  scisports: "from-rose-50 to-rose-100 text-rose-800 border-rose-200",
  transferroom_club: "from-amber-50 to-amber-100 text-amber-800 border-amber-200",
  transferroom_agent: "from-amber-50 to-amber-100 text-amber-800 border-amber-200",
  outlook: "from-sky-50 to-sky-100 text-sky-800 border-sky-200",
  gmail: "from-red-50 to-red-100 text-red-800 border-red-200",
  twilio: "from-teal-50 to-teal-100 text-teal-800 border-teal-200",
  travelpayouts: "from-violet-50 to-violet-100 text-violet-800 border-violet-200",
  booking_com: "from-blue-50 to-blue-100 text-blue-800 border-blue-200",
  skyscanner_apify: "from-indigo-50 to-indigo-100 text-indigo-800 border-indigo-200",
  omio: "from-orange-50 to-orange-100 text-orange-800 border-orange-200",
};

export default function SettingsIntegrations() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [commsItems, setCommsItems] = useState([]);
  const [travelItems, setTravelItems] = useState([]);
  const [drawer, setDrawer] = useState(null); // { provider, mode: "wyscout"|"transferroom"|"twilio" }
  const [toast, setToast] = useState(null);
  const [refreshStatus, setRefreshStatus] = useState(null);

  const refresh = () => Promise.all([
    api.get("/integrations/status").then((r) => setItems(r.data || [])),
    api.get("/comms/catalogue").then((r) => setCommsItems(r.data || [])).catch(() => setCommsItems([])),
    api.get("/travel/catalogue").then((r) => setTravelItems(r.data || [])).catch(() => setTravelItems([])),
    isAdmin && api.get("/admin/refresh-status").then((r) => setRefreshStatus(r.data)).catch(() => setRefreshStatus(null)),
  ]);
  useEffect(() => {
    refresh();
    // surface success when redirected back from OAuth callback
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected")) {
      flash(`${params.get("connected")} connected`);
      window.history.replaceState({}, "", "/settings/integrations");
    }
    /* eslint-disable-next-line */
  }, []);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(null), 3500); };

  const disconnect = async (provider) => {
    if (!window.confirm("Disconnect this integration? Cached data is kept but no new syncs will run.")) return;
    await api.post(`/integrations/${provider}/disconnect`);
    flash("Integration disconnected");
    refresh();
  };

  const commsDisconnect = async (provider) => {
    if (!window.confirm(`Disconnect ${provider}? Connected mailbox/number will be removed.`)) return;
    if (provider === "twilio") {
      await api.post("/comms/twilio/disconnect");
    } else {
      await api.post(`/comms/${provider}/disconnect`);
    }
    flash(`${provider} disconnected`);
    refresh();
  };

  const commsConnect = async (provider) => {
    if (provider === "twilio") {
      setDrawer({ provider: "twilio", mode: "twilio" });
      return;
    }
    try {
      const { data } = await api.get(`/comms/${provider}/start`);
      window.location.href = data.authorize_url;
    } catch (e) {
      flash(e.response?.data?.detail || `${provider} not yet configured — paste keys into backend .env`);
    }
  };

  const commsSync = async (provider) => {
    try {
      const { data } = await api.post(`/comms/${provider}/sync`);
      flash(data.stub ? data.message : `Synced ${data.synced} messages`);
    } catch (e) {
      flash(e.response?.data?.detail || "Sync failed");
    }
  };

  return (
    <Layout title="Integrations" subtitle="Connect the data sources your agency already uses.">
      <div className="space-y-6" data-testid="integrations-root">
        <div className="pm-card p-4 flex items-start gap-3 border-l-4 border-l-emerald-400 bg-emerald-50/30" data-testid="integrations-intro">
          <BadgePercent size={18} className="text-emerald-700 mt-0.5 shrink-0" />
          <div className="text-sm text-slate-700 flex-1">
            <b>Bring-your-own-account.</b> If you already use Wyscout, SciSports or TransferRoom at your agency, plug in the credentials and Playmaker uses them on your behalf.
          </div>
          <button
            onClick={async () => {
              if (!window.confirm("Re-scrape Transfermarkt for every client with a transfermarkt_url? This runs in the background — typically ~2-3 min for the full roster. The wedge will update automatically as each client completes.")) return;
              try {
                const { data } = await api.post("/admin/refresh/transfermarkt");
                flash(`✓ ${data.message || "Backfill queued"} — check Settings → Refresh status, or wait 3 min and reload a client homepage.`);
              } catch (e) {
                flash(e.response?.data?.detail || "Backfill failed");
              }
            }}
            className="pm-btn-secondary h-9 px-3 rounded-xl text-xs font-bold inline-flex items-center gap-1.5 shrink-0"
            data-testid="bulk-resync-transfermarkt"
            disabled={!isAdmin}
            title={isAdmin ? "Re-scrape every client's Transfermarkt URL" : "Admin only"}
          >
            <RefreshCw size={12} /> Refresh all Transfermarkt
          </button>
          <button
            onClick={async () => {
              if (!window.confirm("Re-resolve API-Football team IDs for every client missing one? Uses ~1 API-Football request per client (free plan is 100/day, Pro is 7500/day).")) return;
              try {
                const { data } = await api.post("/admin/relink-team-ids");
                flash(`Team IDs · ${data.linked} linked · ${data.not_found} not found · ${data.processed} processed`);
                if (data.not_found_clubs?.length) {
                  console.log("Not found clubs:", data.not_found_clubs);
                }
              } catch (e) {
                flash(e.response?.data?.detail || "Re-link failed");
              }
            }}
            className="pm-btn-secondary h-9 px-3 rounded-xl text-xs font-bold inline-flex items-center gap-1.5 shrink-0"
            data-testid="relink-team-ids"
            disabled={!isAdmin}
            title={isAdmin ? "Re-resolve API-Football team_id for clients missing one" : "Admin only"}
          >
            <RefreshCw size={12} /> Re-link all team IDs
          </button>
        </div>

        {isAdmin && refreshStatus && (
          <div className="pm-card p-4" data-testid="refresh-schedule-panel">
            <div className="flex items-baseline justify-between mb-3">
              <h3 className="font-display text-lg font-black tracking-tight">Refresh schedule</h3>
              <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${refreshStatus.transfer_window_active ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}>
                {refreshStatus.transfer_window_active ? "🔥 Transfer window active — refresh cadence 5d" : "Off-window — monthly cadence"}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {(refreshStatus.providers || []).map((p) => (
                <div key={p.provider} className="border border-slate-200 rounded-lg px-3 py-2 flex items-center justify-between gap-2" data-testid={`refresh-row-${p.provider}`}>
                  <div className="min-w-0">
                    <div className="font-bold text-xs uppercase tracking-wider">{p.provider}</div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {p.last_run_at ? `Last: ${new Date(p.last_run_at).toLocaleString("en-GB", {day: "numeric", month: "short", hour: "2-digit", minute: "2-digit"})}` : "Never run"}
                      {p.next_due_in_days != null && ` · Next in ${p.next_due_in_days}d`}
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        const { data } = await api.post(`/admin/refresh/${p.provider}`);
                        flash(`${p.provider}: ${data.message || "queued"}`);
                        setTimeout(refresh, 3000);
                      } catch (e) {
                        flash(`${p.provider}: ${e.response?.data?.detail || "queue failed"}`);
                      }
                    }}
                    className="pm-btn-secondary h-7 px-2 rounded text-[10px] font-bold inline-flex items-center gap-1 shrink-0"
                    data-testid={`refresh-now-${p.provider}`}
                  >
                    <RefreshCw size={10} /> Run now
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map((p) => (
            <IntegrationCard
              key={p.provider}
              meta={p}
              onConnect={() => setDrawer({ provider: p.provider })}
              onDisconnect={() => disconnect(p.provider)}
            />
          ))}
        </div>

        {commsItems.length > 0 && (
          <div className="pt-4" data-testid="comms-section">
            <div className="flex items-baseline gap-3 mb-3 mt-2">
              <h2 className="font-display text-2xl font-black tracking-tight">Communications</h2>
              <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">One inbox · all channels</span>
            </div>
            <div className="text-sm text-slate-600 mb-4 leading-relaxed">
              Connect <b>Outlook</b> and <b>Gmail</b> with your own account. Configure <b>Twilio</b> once at the agency level — it powers WhatsApp and SMS through a single phone number.
              {" "}
              <a href="/settings/whatsapp-templates" className="text-emerald-700 hover:underline font-bold" data-testid="link-whatsapp-templates">Manage WhatsApp templates →</a>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {commsItems.map((p) => (
                <CommsCard
                  key={p.provider}
                  meta={p}
                  onConnect={() => commsConnect(p.provider)}
                  onDisconnect={() => commsDisconnect(p.provider)}
                  onSync={() => commsSync(p.provider)}
                />
              ))}
              <ImapCard flash={flash} />
            </div>
          </div>
        )}

        {travelItems.length > 0 && (
          <div className="pt-4" data-testid="travel-section">
            <div className="flex items-baseline gap-3 mb-3 mt-2">
              <h2 className="font-display text-2xl font-black tracking-tight">Travel</h2>
              <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Real flights · hotels · trains</span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {travelItems.map((p) => (
                <TravelCard
                  key={p.provider}
                  meta={p}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {drawer && drawer.provider === "twilio" && (
        <TwilioConnectDrawer
          onClose={() => setDrawer(null)}
          onDone={() => { setDrawer(null); refresh(); flash("Twilio configured for the agency"); }}
        />
      )}

      {drawer && drawer.provider !== "twilio" && (
        <ConnectDrawer
          providerKey={drawer.provider}
          meta={items.find((i) => i.provider === drawer.provider)}
          onClose={() => setDrawer(null)}
          onDone={() => { setDrawer(null); refresh(); flash("Integration connected"); }}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 bg-slate-900 text-white px-4 py-3 rounded-xl shadow-2xl flex items-center gap-2 z-50" data-testid="integrations-toast">
          <Check size={14} className="text-emerald-400" />
          <span className="text-sm font-bold">{toast}</span>
        </div>
      )}
    </Layout>
  );
}

function IntegrationCard({ meta, onConnect, onDisconnect }) {
  const Icon = PROVIDER_ICONS[meta.provider] || Plug;
  const hue = PROVIDER_HUES[meta.provider] || "from-slate-50 to-slate-100 text-slate-800 border-slate-200";
  const connected = meta.connected;
  return (
    <div className="pm-card overflow-hidden flex flex-col" data-testid={`integration-card-${meta.provider}`}>
      <div className={`p-5 bg-gradient-to-br ${hue} border-b`}>
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/70 backdrop-blur flex items-center justify-center shrink-0">
            <Icon size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-70">{meta.category}</div>
            <div className="font-display text-xl font-black tracking-tight">{meta.name}</div>
          </div>
          {connected ? (
            <span className="pm-pill pm-pill-green text-[10px] inline-flex items-center gap-1" data-testid={`integration-${meta.provider}-status`}>
              <Check size={10} /> Connected
            </span>
          ) : (
            <span className="pm-pill pm-pill-slate text-[10px]" data-testid={`integration-${meta.provider}-status`}>Not connected</span>
          )}
        </div>
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <p className="text-sm text-slate-700 leading-relaxed">{meta.description}</p>
        {connected && meta.account_label && (
          <div className="text-[11px] text-slate-500 mt-4 inline-flex items-center gap-1.5" data-testid={`integration-${meta.provider}-account`}>
            <Lock size={10} /> Connected as <span className="font-mono-tab text-slate-700 font-bold">{meta.account_label}</span>
          </div>
        )}
        <div className="mt-5 flex items-center gap-2 flex-wrap">
          {!meta.platform_managed && !connected && (
            <button
              onClick={onConnect}
              className="pm-btn-primary h-10 px-4 rounded-xl text-sm font-bold flex items-center gap-2"
              data-testid={`integration-${meta.provider}-connect`}
            >
              <Plug size={12} /> Connect existing account
            </button>
          )}
          {!meta.platform_managed && connected && (
            <>
              <button
                onClick={onConnect}
                className="pm-btn-secondary h-10 px-4 rounded-xl text-sm font-bold flex items-center gap-2"
                data-testid={`integration-${meta.provider}-manage`}
              >
                <RefreshCw size={12} /> Manage
              </button>
              <button
                onClick={onDisconnect}
                className="pm-btn-secondary h-10 px-3 rounded-xl text-sm font-bold flex items-center gap-1.5 text-red-700 border-red-200 hover:bg-red-50"
                data-testid={`integration-${meta.provider}-disconnect`}
              >
                <X size={12} /> Disconnect
              </button>
            </>
          )}
          {meta.platform_managed && (
            <div className="pm-pill pm-pill-amber text-[10px]" data-testid={`integration-${meta.provider}-stub-pill`}>
              {meta.connected ? "LIVE — platform managed" : "STUB — awaiting API key"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ConnectDrawer({ providerKey, meta, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const isWyscout = providerKey === "wyscout";
  const isTransferRoom = providerKey === "transferroom_club" || providerKey === "transferroom_agent";
  const isSciSports = providerKey === "scisports";
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [apiKey, setApiKey] = useState("");

  const submit = async () => {
    setErr(null); setBusy(true);
    try {
      if (isWyscout) {
        if (!username.trim() || !password) throw new Error("Both username and password are required");
        await api.post("/integrations/wyscout/connect", { username: username.trim(), password });
      } else if (isTransferRoom) {
        if (!apiKey.trim()) throw new Error("API key is required");
        await api.post("/integrations/transferroom/connect", { api_key: apiKey.trim() });
      } else if (isSciSports) {
        if (!apiKey.trim()) throw new Error("API key is required");
        await api.post("/integrations/scisports/connect", { api_key: apiKey.trim() });
      } else {
        throw new Error("This provider is platform-managed — no credentials required.");
      }
      onDone();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-testid="connect-drawer">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-start gap-3">
          <Lock size={18} className="text-emerald-700 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Connect</div>
            <div className="font-display text-lg font-bold tracking-tight">{meta.name}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 p-1" data-testid="connect-close"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-slate-600 leading-relaxed">
            Your credentials are encrypted with AES-256 before being saved. They are never logged or shown in plain text and are only used to call the {meta.name} API on your behalf.
          </p>
          {isWyscout && (
            <>
              <Field label="Wyscout username" testid="wy-user">
                <input className="pm-input" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="you@club.com" autoComplete="username" />
              </Field>
              <Field label="Wyscout password" testid="wy-pass">
                <input className="pm-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" />
              </Field>
            </>
          )}
          {isTransferRoom && (
            <Field label="TransferRoom API key" testid="tr-key">
              <input className="pm-input" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="tr_live_…" autoComplete="off" />
            </Field>
          )}
          {isSciSports && (
            <Field label="SciSports API key" testid="sci-key">
              <input className="pm-input" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="sci_live_…" autoComplete="off" />
            </Field>
          )}
          {err && (
            <div className="text-xs text-red-700 flex items-center gap-1.5" data-testid="connect-error">
              <AlertCircle size={12} /> {err}
            </div>
          )}
        </div>
        <div className="px-5 py-4 border-t border-slate-200 flex items-center gap-2 bg-slate-50">
          <button onClick={onClose} className="pm-btn-secondary h-10 px-4 rounded-xl text-sm font-bold">Cancel</button>
          <div className="flex-1" />
          <button onClick={submit} disabled={busy} className="pm-btn-primary h-10 px-5 rounded-xl text-sm font-bold flex items-center gap-1.5 disabled:opacity-40" data-testid="connect-submit">
            <Plug size={12} /> {busy ? "Connecting…" : "Save & connect"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function TravelCard({ meta }) {
  const Icon = PROVIDER_ICONS[meta.provider] || Plane;
  const hue = PROVIDER_HUES[meta.provider] || "from-slate-50 to-slate-100 text-slate-800 border-slate-200";
  return (
    <div className="pm-card overflow-hidden flex flex-col" data-testid={`travel-card-${meta.provider}`}>
      <div className={`p-5 bg-gradient-to-br ${hue} border-b`}>
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/70 backdrop-blur flex items-center justify-center shrink-0">
            <Icon size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-70">{meta.category}</div>
            <div className="font-display text-xl font-black tracking-tight">{meta.name}</div>
          </div>
          {meta.connected ? (
            <span className="pm-pill pm-pill-green text-[10px] inline-flex items-center gap-1" data-testid={`travel-${meta.provider}-status`}>
              <Check size={10} /> Live
            </span>
          ) : (
            <span className="pm-pill pm-pill-amber text-[10px]" data-testid={`travel-${meta.provider}-status`}>Stub mode</span>
          )}
        </div>
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <p className="text-sm text-slate-700 leading-relaxed">{meta.description}</p>
        {!meta.connected && (
          <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2 mt-4 flex items-start gap-1.5" data-testid={`travel-${meta.provider}-stub`}>
            <AlertCircle size={11} className="mt-0.5 shrink-0" />
            <span>{
              meta.provider === "travelpayouts" ? "Paste TRAVELPAYOUTS_MARKER into backend/.env after signup."
              : meta.provider === "booking_com" ? "Paste BOOKING_API_TOKEN + BOOKING_AFFILIATE_ID into backend/.env."
              : meta.provider === "skyscanner_apify" ? "Set APIFY_TOKEN in backend/.env to enable live Skyscanner scrapes."
              : "Routing traffic through provider links."
            }</span>
          </div>
        )}
      </div>
    </div>
  );
}

function CommsCard({ meta, onConnect, onDisconnect, onSync }) {
  const Icon = PROVIDER_ICONS[meta.provider] || Mail;
  const hue = PROVIDER_HUES[meta.provider] || "from-slate-50 to-slate-100 text-slate-800 border-slate-200";
  const connected = meta.connected;
  const isOAuth = meta.provider === "outlook" || meta.provider === "gmail";
  return (
    <div className="pm-card overflow-hidden flex flex-col" data-testid={`comms-card-${meta.provider}`}>
      <div className={`p-5 bg-gradient-to-br ${hue} border-b`}>
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/70 backdrop-blur flex items-center justify-center shrink-0">
            <Icon size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-70">{meta.category}</div>
            <div className="font-display text-xl font-black tracking-tight">{meta.name}</div>
          </div>
          {connected ? (
            <span className="pm-pill pm-pill-green text-[10px] inline-flex items-center gap-1" data-testid={`comms-${meta.provider}-status`}>
              <Check size={10} /> Connected
            </span>
          ) : (
            <span className="pm-pill pm-pill-slate text-[10px]" data-testid={`comms-${meta.provider}-status`}>Not connected</span>
          )}
        </div>
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <p className="text-sm text-slate-700 leading-relaxed">{meta.description}</p>
        {connected && meta.account_label && (
          <div className="text-[11px] text-slate-500 mt-4 inline-flex items-center gap-1.5" data-testid={`comms-${meta.provider}-account`}>
            <Lock size={10} /> {isOAuth ? "Mailbox " : "From "} <span className="font-mono-tab text-slate-700 font-bold">{meta.account_label}</span>
          </div>
        )}
        {meta.stub && !connected && (
          <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2 mt-4 flex items-start gap-1.5" data-testid={`comms-${meta.provider}-stub`}>
            <AlertCircle size={11} className="mt-0.5 shrink-0" />
            <span>Stub — paste real {meta.provider === "outlook" ? "AZURE_CLIENT_ID" : meta.provider === "gmail" ? "GOOGLE_CLIENT_ID" : "TWILIO_ACCOUNT_SID"} into <code className="font-mono-tab">backend/.env</code> to go live.</span>
          </div>
        )}
        <div className="mt-5 flex items-center gap-2 flex-wrap">
          {!connected && (
            <button
              onClick={onConnect}
              className="pm-btn-primary h-10 px-4 rounded-xl text-sm font-bold flex items-center gap-2"
              data-testid={`comms-${meta.provider}-connect`}
            >
              <Plug size={12} /> {meta.provider === "twilio" ? "Configure Twilio" : "Connect " + meta.name.split(" ")[0]}
            </button>
          )}
          {connected && isOAuth && (
            <button
              onClick={onSync}
              className="pm-btn-secondary h-10 px-4 rounded-xl text-sm font-bold flex items-center gap-2"
              data-testid={`comms-${meta.provider}-sync`}
            >
              <RefreshCw size={12} /> Sync inbox
            </button>
          )}
          {connected && (
            <button
              onClick={onDisconnect}
              className="pm-btn-secondary h-10 px-3 rounded-xl text-sm font-bold flex items-center gap-1.5 text-red-700 border-red-200 hover:bg-red-50"
              data-testid={`comms-${meta.provider}-disconnect`}
            >
              <X size={12} /> Disconnect
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function TwilioConnectDrawer({ onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [sid, setSid] = useState("");
  const [token, setToken] = useState("");
  const [from, setFrom] = useState("");
  const [waNum, setWaNum] = useState("");

  const submit = async () => {
    setErr(null); setBusy(true);
    try {
      if (!sid.trim() || !token.trim() || !from.trim()) throw new Error("Account SID, Auth Token and From number are required");
      await api.post("/comms/twilio/connect", {
        account_sid: sid.trim(), auth_token: token.trim(),
        from_number: from.trim(), whatsapp_number: waNum.trim() || from.trim(),
      });
      onDone();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-testid="twilio-drawer">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-start gap-3">
          <Phone size={18} className="text-teal-700 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Configure</div>
            <div className="font-display text-lg font-bold tracking-tight">Twilio · WhatsApp + SMS</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 p-1" data-testid="twilio-close"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-slate-600 leading-relaxed">
            Paste credentials from twilio.com → Account → API Keys. Both tokens are encrypted with AES-256 before being stored. One number covers WhatsApp + SMS + Voice.
          </p>
          <Field label="Account SID" testid="tw-sid">
            <input className="pm-input" value={sid} onChange={(e) => setSid(e.target.value)} placeholder="ACxxxxxxxxxxxxxxxxxxxx" autoComplete="off" />
          </Field>
          <Field label="Auth Token" testid="tw-token">
            <input className="pm-input" type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="••••••••" autoComplete="off" />
          </Field>
          <Field label="From number (E.164, +xxxxxxxxxxx)" testid="tw-from">
            <input className="pm-input" value={from} onChange={(e) => setFrom(e.target.value)} placeholder="+31201234567" />
          </Field>
          <Field label="WhatsApp number (defaults to From number)" testid="tw-wa">
            <input className="pm-input" value={waNum} onChange={(e) => setWaNum(e.target.value)} placeholder="+31201234567" />
          </Field>
          {err && (
            <div className="text-xs text-red-700 flex items-center gap-1.5" data-testid="twilio-error">
              <AlertCircle size={12} /> {err}
            </div>
          )}
        </div>
        <div className="px-5 py-4 border-t border-slate-200 flex items-center gap-2 bg-slate-50">
          <button onClick={onClose} className="pm-btn-secondary h-10 px-4 rounded-xl text-sm font-bold">Cancel</button>
          <div className="flex-1" />
          <button onClick={submit} disabled={busy} className="pm-btn-primary h-10 px-5 rounded-xl text-sm font-bold flex items-center gap-1.5 disabled:opacity-40" data-testid="twilio-submit">
            <Plug size={12} /> {busy ? "Saving…" : "Save & enable"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════ IMAP — connect any email ════════════════════════ */

function ImapCard({ flash }) {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get("/comms/imap/status");
      setStatus(r.data);
    } catch (e) {
      setStatus({ connected: false });
    }
  };
  useEffect(() => { refresh(); }, []);

  const onSync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/comms/imap/sync");
      flash(`Synced ${r.data.synced} message${r.data.synced === 1 ? "" : "s"}`);
      await refresh();
    } catch (e) {
      flash(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const onDisconnect = async () => {
    if (!window.confirm("Disconnect this mailbox? Cached messages are kept, but new mail will stop syncing.")) return;
    await api.post("/comms/imap/disconnect");
    flash("Mailbox disconnected");
    await refresh();
  };

  const connected = !!status?.connected;
  const conn = status?.connection || {};

  return (
    <>
      <div
        className="rounded-2xl border border-slate-200 bg-gradient-to-br from-indigo-50 to-indigo-100 p-5 flex flex-col gap-3 relative overflow-hidden"
        data-testid="imap-card"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-white border border-indigo-200 flex items-center justify-center text-indigo-700 shrink-0">
            <Mail size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-display font-black tracking-tight text-lg text-indigo-900">Any email (IMAP)</div>
              {connected && <span className="pm-pill pm-pill-green text-[10px]" data-testid="imap-connected-pill">Connected</span>}
              <span className="pm-pill bg-white text-indigo-700 border-indigo-200 text-[10px]">Universal</span>
            </div>
            <div className="text-xs text-indigo-800/80 mt-0.5 truncate">
              {connected
                ? <>Connected as <b>{conn.email}</b>{conn.preset_label ? ` · ${conn.preset_label}` : ""}</>
                : "Connect Gmail-on-custom-domain, Apple Mail, Fastmail, ProtonMail Bridge, Yahoo, or any self-hosted IMAP server."}
            </div>
          </div>
        </div>

        {connected && (
          <div className="text-[11px] text-indigo-700/80 grid grid-cols-2 gap-x-3 gap-y-1 mt-1 font-mono-tab">
            <div><span className="opacity-60">IMAP:</span> {conn.imap_host}:{conn.imap_port}</div>
            <div><span className="opacity-60">SMTP:</span> {conn.smtp_host}:{conn.smtp_port}</div>
            <div className="col-span-2 opacity-70 flex items-center gap-2" data-testid="imap-last-sync">
              <span>
                {conn.last_synced_at
                  ? `Last sync: ${new Date(conn.last_synced_at).toLocaleString()}${conn.last_sync_source === "idle_push" ? " (push)" : ""}`
                  : "Not synced yet — IDLE listener starting…"}
              </span>
              {status?.idle?.running ? (
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold text-[10px]"
                  title="IMAP IDLE push connection is open — new mail arrives within seconds"
                  data-testid="imap-idle-pill"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> PUSH LIVE
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-bold text-[10px]" title="Falling back to 15-minute polling">
                  POLL 15 MIN
                </span>
              )}
            </div>
            {conn.last_sync_error && (
              <div className="col-span-2 text-red-700 font-bold" data-testid="imap-last-sync-error">
                ⚠ Last sync error: {conn.last_sync_error}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 mt-1">
          {!connected ? (
            <button
              onClick={() => setOpen(true)}
              className="pm-btn-primary h-9 px-4 rounded-lg text-xs font-bold flex items-center gap-1.5"
              data-testid="imap-connect-btn"
            >
              <Plug size={12} /> Connect mailbox
            </button>
          ) : (
            <>
              <button
                onClick={onSync}
                disabled={syncing}
                className="pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1 disabled:opacity-50"
                data-testid="imap-sync-btn"
              >
                <RefreshCw size={12} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing…" : "Sync inbox"}
              </button>
              <button
                onClick={onDisconnect}
                className="h-9 px-3 rounded-lg text-xs font-bold text-red-700 hover:bg-red-50 flex items-center gap-1"
                data-testid="imap-disconnect-btn"
              >
                <X size={12} /> Disconnect
              </button>
            </>
          )}
        </div>
      </div>

      {open && (
        <ImapConnectModal
          onClose={() => setOpen(false)}
          onConnected={async () => {
            setOpen(false);
            flash("Mailbox connected — initial sync running");
            await refresh();
          }}
        />
      )}
    </>
  );
}

function ImapConnectModal({ onClose, onConnected }) {
  const [presets, setPresets] = useState({});
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [advanced, setAdvanced] = useState(false);
  const [overrides, setOverrides] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/comms/imap/presets").then((r) => setPresets(r.data || {})).catch(() => {});
  }, []);

  const preset = (() => {
    const domain = (email.split("@")[1] || "").toLowerCase();
    return presets[domain] || null;
  })();

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/comms/imap/connect", {
        email_addr: email,
        password,
        ...overrides,
      });
      onConnected();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "manual_required") setAdvanced(true);
      setErr(formatApiErrorDetail(d) || "Connection failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="imap-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Connect mailbox</div>
            <div className="font-display text-lg font-black tracking-tight">Any email · IMAP / SMTP</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="imap-modal-close"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Email address">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yourcompany.com"
              className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm"
              data-testid="imap-email-input"
              autoFocus
            />
          </Field>

          {preset && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2 text-xs text-emerald-900" data-testid="imap-preset-banner">
              <div className="font-bold">{preset.label} detected — settings auto-filled</div>
              {preset.needs_app_password && (
                <div className="mt-1">
                  ⚠️ Requires an <b>App Password</b>, not your regular password.
                  {" "}
                  {preset.help_url && (
                    <a href={preset.help_url} target="_blank" rel="noreferrer" className="underline font-bold">Generate one →</a>
                  )}
                </div>
              )}
              {preset.help && !preset.needs_app_password && <div className="mt-1">{preset.help}</div>}
            </div>
          )}

          <Field label={preset?.needs_app_password ? "App password" : "Password"}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={preset?.needs_app_password ? "16-character app password" : "Password"}
              className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm font-mono-tab"
              data-testid="imap-password-input"
            />
          </Field>

          {!preset && email.includes("@") && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2 text-xs text-amber-900">
              We don't have a preset for this provider — please fill the IMAP/SMTP server details below.
            </div>
          )}

          <button
            type="button"
            onClick={() => setAdvanced((v) => !v)}
            className="text-xs font-bold text-slate-600 hover:text-slate-900"
            data-testid="imap-advanced-toggle"
          >
            {advanced ? "Hide" : "Show"} advanced settings (server hosts/ports)
          </button>

          {(advanced || !preset) && (
            <div className="space-y-2 rounded-lg bg-slate-50 px-3 py-3 border border-slate-200">
              <div className="grid grid-cols-2 gap-2">
                <ServerField label="IMAP host" placeholder={preset?.imap_host || "imap.example.com"} value={overrides.imap_host} onChange={(v) => setOverrides((s) => ({ ...s, imap_host: v }))} testid="imap-host" />
                <ServerField label="IMAP port" placeholder={String(preset?.imap_port || 993)} value={overrides.imap_port} onChange={(v) => setOverrides((s) => ({ ...s, imap_port: v ? Number(v) : null }))} testid="imap-port" />
                <ServerField label="SMTP host" placeholder={preset?.smtp_host || "smtp.example.com"} value={overrides.smtp_host} onChange={(v) => setOverrides((s) => ({ ...s, smtp_host: v }))} testid="smtp-host" />
                <ServerField label="SMTP port" placeholder={String(preset?.smtp_port || 587)} value={overrides.smtp_port} onChange={(v) => setOverrides((s) => ({ ...s, smtp_port: v ? Number(v) : null }))} testid="smtp-port" />
              </div>
              <div className="text-[10px] text-slate-500">Defaults: IMAP 993 (SSL), SMTP 587 (STARTTLS). Leave blank to use the preset.</div>
            </div>
          )}

          {err && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 flex items-start gap-1.5" data-testid="imap-error">
              <AlertCircle size={12} className="mt-0.5 shrink-0" />
              <span>{err}</span>
            </div>
          )}
        </div>
        <div className="px-5 py-4 flex items-center gap-3 border-t border-slate-100">
          <button onClick={onClose} className="pm-btn-secondary h-10 px-4 rounded-xl text-sm font-bold">Cancel</button>
          <div className="flex-1" />
          <button
            onClick={submit}
            disabled={busy || !email || !password}
            className="pm-btn-primary h-10 px-5 rounded-xl text-sm font-bold flex items-center gap-1.5 disabled:opacity-40"
            data-testid="imap-submit"
          >
            <Plug size={12} /> {busy ? "Connecting…" : "Connect & sync"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ServerField({ label, placeholder, value, onChange, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">{label}</div>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-9 px-2.5 rounded-lg border border-slate-200 text-xs font-mono-tab"
        data-testid={testid}
      />
    </div>
  );
}
